// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface PropWarningModel {
    fields: {
        propWarningTypeKey: string;
        propWarningTypeDisplayMessage: string;
    };
}

const enum fields {
    propWarningTypes = "propWarningTypes",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.propWarningTypes]: PropWarningModel[];
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    propType: getProductState(state, state.pages.productDetails.selectedProductId).value?.properties.prop65,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface PropWarningStyles {
    container?: GridContainerProps;
    iconGrid?: GridItemProps;
    messageGrid?: GridItemProps;
    iconStyles?: IconPresentationProps;
    messageHeadingStyles?: TypographyPresentationProps;
    messageStyles?: TypographyPresentationProps;
}

export const propWarningStyles: PropWarningStyles = {
    container: {
        css: css`
            display: flex;
            margin: ${pxToRem(24)} auto ${pxToRem(2)} auto;
            padding: 2%;
            border: ${pxToRem(1)} solid ${getColor("primary.dark")};
            border-radius: ${pxToRem(4)};
            background-color: ${getColor("background.alt")};
        `,
    },
    iconGrid: {
        width: [12, 1, 1, 1, 1],
        align: "middle",
    },
    messageGrid: {
        width: [12, 11, 11, 11, 11],
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    iconStyles: {
        size: 36,
        css: css`
            color: ${getColor("primary.dark")};
        `,
    },
    messageHeadingStyles: {
        transform: "uppercase",
        variant: "h3",
        css: css`
            font-size: ${pxToRem(20)} !important;
        `,
    },
    messageStyles: {
        color: "tertiary",
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        font-size: ${pxToRem(20)} !important;
                    `,
                    css`
                        font-size: ${pxToRem(20)} !important;
                    `,
                    css`
                        font-size: ${pxToRem(20)} !important;
                    `,
                ]);
            }}
        `,
    },
};

const styles = propWarningStyles;

const getPropMessage = (propType: string | undefined, fields: PropWarningModel[]) => {
    let arr = [];
    let filteredArr: (string | null)[] = [];
    if (propType) {
        arr = fields.map(obj => {
            if (obj.fields.propWarningTypeKey === propType) {
                return obj.fields.propWarningTypeDisplayMessage;
            }
            return null;
        });
        filteredArr = arr.filter(function (el: any) {
            return el != null;
        });
    }
    return filteredArr[0];
};

const PropWarning: FC<Props> = ({ propType, fields }) => {
    const [htmlContent, setHtmlContent] = useState<string | null>("");

    useEffect(() => {
        setHtmlContent(getPropMessage(propType, fields.propWarningTypes));
    }, [propType]);

    return htmlContent ? (
        <GridContainer {...styles.container}>
            <GridItem {...styles.iconGrid}>
                <IconMemo {...styles.iconStyles} src={AlertTriangle} />
            </GridItem>
            <GridItem {...styles.messageGrid}>
                <Typography {...styles.messageHeadingStyles}>{translate("Warning")}</Typography>
                <Typography {...styles.messageStyles}>
                    {htmlContent}For more information go to{" "}
                    <Link href="https://www.P65Warnings.ca.gov">www.P65Warnings.ca.gov.</Link>
                </Typography>
            </GridItem>
        </GridContainer>
    ) : null;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(PropWarning),
    definition: {
        group: "Product Details",
        icon: "AlertTriangle",
        fieldDefinitions: [
            {
                name: fields.propWarningTypes,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.propWarningTypeKey;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "propWarningTypeKey",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "propWarningTypeDisplayMessage",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
